import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en } from './locales/en';
import { de } from './locales/de';
import { weightToPercent } from '@shared/weights.mjs';

/**
 * Two languages, one dictionary shape.
 *
 * `en` is the source of truth: its keys type the German file, so a missing
 * translation is a build error rather than a blank label in front of a customer.
 */

export type Lang = 'en' | 'de';
export type Dictionary = typeof en;

const DICTIONARIES: Record<Lang, Dictionary> = { en, de };
const STORAGE_KEY = 'hs.lang';

type I18nValue = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Dictionary;
  /** Fill {placeholders} in a translated string. */
  fill: (template: string, values: Record<string, string | number>) => string;
  formatDate: (value: string | number | Date | null | undefined, opts?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number, opts?: Intl.NumberFormatOptions) => string;
  /** A stored weight (a fraction of one whole, 0.2) as the percentage people read: "20%". */
  formatWeight: (weight: number) => string;
  formatRelative: (value: string | number | Date | null | undefined) => string;
  /** A project status ('draft' | 'scoring' | 'locked' | 'decided') in the current language. */
  statusLabel: (status: string) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

// The marketing site mirrors every page at `/de/...`; that path is an
// explicit statement of language, prerendered per locale (see
// `web/scripts/prerender.mjs`). The signed-in app, the vendor portal, the
// admin console and embeds carry no language in their URL at all, so those
// are the only places a remembered preference should decide anything.
function isRouteForcedByPath(path: string): boolean {
  return !/^\/(app|portal|admin|embed)(\/|$)/.test(path);
}

function initialLang(): Lang {
  if (typeof window === 'undefined') return 'en';
  const path = window.location.pathname;
  // An explicit language in the URL wins over a remembered preference —
  // checked first, not last, so a visitor who once chose English still gets
  // German content (and a German `lang`) on `/de/...`, and vice versa.
  if (isRouteForcedByPath(path)) {
    return path === '/de' || path.startsWith('/de/') ? 'de' : 'en';
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    /* private mode */
  }
  const nav = window.navigator.language || '';
  return nav.toLowerCase().startsWith('de') ? 'de' : 'en';
}

export function I18nProvider({ children, lang: forced }: { children: ReactNode; lang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => forced || initialLang());

  useEffect(() => {
    // `document.documentElement.lang` must always match what is on screen,
    // forced route or not — a screen reader does not care which provider
    // decided the copy. Only the *stored preference* is route-conditional:
    // a forced route (the language toggle on `/de/pricing`, say) may change
    // what is displayed without overwriting what an un-forced route (the
    // app, the portal) remembers next time.
    document.documentElement.setAttribute('lang', lang);
    if (forced) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* private mode */
    }
  }, [lang, forced]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);

  const value = useMemo<I18nValue>(() => {
    const locale = lang === 'de' ? 'de-DE' : 'en-GB';
    const dict = DICTIONARIES[lang];
    return {
      lang,
      setLang,
      t: dict,
      fill: (template, values) =>
        template.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match)),
      statusLabel: (status) => {
        const map: Record<string, string> = {
          draft: dict.common.statusDraft,
          scoring: dict.common.statusScoring,
          locked: dict.common.statusLocked,
          decided: dict.common.statusDecided,
        };
        return map[status] || status;
      },
      formatNumber: (value, opts) => new Intl.NumberFormat(locale, opts).format(value),
      formatWeight: (weight) =>
        new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(weightToPercent(weight) / 100),
      formatDate: (value, opts) => {
        if (!value) return '—';
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return new Intl.DateTimeFormat(locale, opts || { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
      },
      formatRelative: (value) => {
        if (!value) return '—';
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        const diff = date.getTime() - Date.now();
        const abs = Math.abs(diff);
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        const minute = 60_000;
        const hour = 3_600_000;
        const day = 86_400_000;
        if (abs < minute) return rtf.format(Math.round(diff / 1000), 'second');
        if (abs < hour) return rtf.format(Math.round(diff / minute), 'minute');
        if (abs < day) return rtf.format(Math.round(diff / hour), 'hour');
        if (abs < day * 30) return rtf.format(Math.round(diff / day), 'day');
        return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
      },
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}
